import { z } from 'zod';
/**
 * Selfie characters.
 *
 * The rules around this are deliberately tight, because the subject is a
 * photograph of a child:
 *
 *  1. The photo never leaves the family's own server. No third party, no AI
 *     service, no "cartoonify this face" API call - ever. Any stylised look is
 *     computed in the browser on the device that took it.
 *  2. It is never shown before login. The profile picker answers only on a
 *     device a parent has signed in on, and even there it always shows the
 *     drawn avatar: a child's face is for signed-in screens only.
 *  3. A parent can switch the whole feature off, and can delete any photo.
 */
export const AVATAR_KINDS = ['drawn', 'photo'];
export const avatarKindSchema = z.enum(AVATAR_KINDS);
export const PHOTO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** The client downscales to this square before encoding. Big enough to look
 *  good on an iPad, small enough that the whole thing is a few tens of KB. */
export const PHOTO_EDGE_PX = 512;
/** Generous ceiling on the decoded bytes; a 512px JPEG lands far under it. */
export const PHOTO_MAX_BYTES = 600_000;
const dataUrlPattern = /^data:(image\/(?:jpeg|png|webp));base64,([A-Za-z0-9+/]+={0,2})$/;
export const uploadPhotoInput = z.object({
    /** A data URL produced by canvas.toDataURL, which is also what strips the
     *  original EXIF: re-encoding through a canvas drops camera metadata,
     *  including any GPS tag the device attached. */
    dataUrl: z
        .string()
        .max(Math.ceil((PHOTO_MAX_BYTES * 4) / 3) + 64)
        .refine((v) => dataUrlPattern.test(v), 'Expected a JPEG, PNG or WebP data URL'),
});
/**
 * Decodes and sanity-checks a photo data URL. Checks the declared type against
 * the actual magic bytes, because a caller that says "jpeg" and sends something
 * else is either broken or hostile.
 */
export function decodePhotoDataUrl(dataUrl) {
    const match = dataUrlPattern.exec(dataUrl);
    if (!match)
        throw new Error('Not a supported image data URL');
    const mime = match[1];
    const bytes = base64ToBytes(match[2]);
    if (bytes.length === 0)
        throw new Error('That image is empty');
    if (bytes.length > PHOTO_MAX_BYTES)
        throw new Error('That image is too large');
    if (!magicBytesMatch(mime, bytes))
        throw new Error('That file is not really a ' + mime);
    return { mime, bytes };
}
const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
/** Hand-rolled so this module stays free of both `Buffer` and `atob`: it is
 *  imported by the browser bundle as well as the server. */
function base64ToBytes(input) {
    const clean = input.replace(/=+$/, '');
    const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
    let bits = 0;
    let value = 0;
    let index = 0;
    for (const char of clean) {
        const digit = BASE64_ALPHABET.indexOf(char);
        if (digit === -1)
            throw new Error('That image is not valid base64');
        value = (value << 6) | digit;
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            out[index++] = (value >> bits) & 0xff;
        }
    }
    return out.subarray(0, index);
}
function magicBytesMatch(mime, b) {
    if (mime === 'image/jpeg')
        return b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff;
    if (mime === 'image/png') {
        return b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;
    }
    // WebP: "RIFF" .... "WEBP"
    return (b[0] === 0x52 &&
        b[1] === 0x49 &&
        b[2] === 0x46 &&
        b[3] === 0x46 &&
        b[8] === 0x57 &&
        b[9] === 0x45 &&
        b[10] === 0x42 &&
        b[11] === 0x50);
}
export const extensionFor = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
};
/** Looks a child can give their selfie. All of these are plain canvas pixel
 *  maths that runs on the device - none of them involves a network call. */
export const PHOTO_LOOKS = [
    { key: 'natural', label: 'Just me' },
    { key: 'poster', label: 'Poster' },
    { key: 'sunny', label: 'Sunny' },
    { key: 'comic', label: 'Comic' },
    { key: 'mono', label: 'Moonlight' },
];
//# sourceMappingURL=photos.js.map